import { FormEvent, useEffect, useState } from "react";



import { createProperty, editProperty } from "@/api/property";
import { useMutation, useQueryClient } from "@tanstack/react-query";



import { PropertyMutationPayload } from "@/types/form/property";



import { usePropertyModalStore } from "@/hooks/store/use-property-modal-store";



import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import Modal from "@/components/ui/modal";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";


export const formInputType = {
  "1": {
    input: "number",
    type: "number",
    name: "number"
  },
  "2": {
    input: "range",
    type: "range",
    name: "number"
  },
  "3": {
    input: "text",
    type: "short-text",
    name: "number"
  },
  "4": {
    input: "text",
    type: "long-text",
    name: "number"
  },
  "5": {
    input: "checkbox",
    type: "checkbox",
    name: "number"
  },
  "6": {
    input: "text",
    type: "date",
    name: "number"
  },
  "7": {
    input: "file",
    type: "file",
    name: "number"
  },
  "8": {
    input: "color",
    type: "color",
    name: "number"
  },
  "9": {
    input: "email",
    type: "email",
    name: "number"
  },
  "10": {
    input: "password",
    type: "password",
    name: "number"
  },
  "11": {
    input: "text",
    type: "select",
    name: "select"
  },
  "12": {
    input: "text",
    type: "editor",
    name: "editor"
  },
  "13": {
    input: "text",
    type: "price",
    name: "price"
  },
  "14": {
    input: "file",
    type: "image-preview",
    name: "image-preview"
  },
  "15": {
    input: "radio",
    type: "radiobutton",
    name: "radiobutton"
  }
};

const PropertyModal = () => {
  const queryClient = useQueryClient();

  const { isOpen, onClose, property, entityId } = usePropertyModalStore();

  const [options, setOptions] = useState<string[]>([]);

  const [selectedType, setSelectedType] = useState<keyof typeof formInputType>(
    (property?.type as keyof typeof formInputType) ?? "1"
  );

  const [allowNull, setAllowNull] = useState<boolean>(
    property?.allowNull ?? false
  );

  const { mutate, isPending } = useMutation({
    mutationFn: (data: PropertyMutationPayload) =>
      property ? editProperty(data) : createProperty(data),

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["properties"]
      });

      onClose();
    }
  });

  const titleText = property
    ? `ویرایش عنصر ${property.previewName}`
    : "ساخت عنصر جدید";

  const submitBtnText = property ? "ویرایش عنصر" : "ساخت عنصر جدید";

  useEffect(() => {
    setAllowNull(property?.allowNull ?? false);

    if (property?.Options) {
      try {
        const parsedOptions =
          typeof property.Options === "string"
            ? JSON.parse(property.Options)
            : property.Options;

        setOptions(parsedOptions ?? []);
      } catch {
        setOptions([]);
      }
    } else {
      setOptions([]);
    }
  }, [isOpen, property]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const formData = new FormData(e.currentTarget);

    const getFieldValue = (key: string) => {
      const value = formData.get(key);

      return typeof value === "string" ? value : undefined;
    };

    const resolvedEntityId = Number(entityId ?? property?.entityId ?? 0);

    const payload: PropertyMutationPayload = {
      Id: property?.id,

      PreviewName: getFieldValue("previewName") ?? "",

      PropertyName: getFieldValue("propertyName") ?? "",

      Description: getFieldValue("description") ?? "",

      Type: getFieldValue("type") ?? "",

      DefaultValue: getFieldValue("defaultValue") ?? "",

      EntityId: resolvedEntityId,

      IsNullable: allowNull,

      AllowedFileExtensions: getFieldValue("allowedFileExtensions"),

      Options:
        selectedType === "11" || selectedType === "15"
          ? JSON.stringify(options.filter((item) => item.trim() !== ""))
          : undefined,

      Length: (() => {
        const raw = getFieldValue("length");

        const num = raw ? Number(raw) : undefined;

        return typeof num === "number" && !Number.isNaN(num) && num > 0
          ? num
          : undefined;
      })()
    };

    mutate(payload);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} description="" title={titleText}>
      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="grid grid-cols-2 gap-x-10">
          <div className="flex flex-col gap-y-1">
            <label className="text-sm">نام نمایشی</label>

            <Input
              type="text"
              name="previewName"
              defaultValue={property?.previewName ?? ""}
              placeholder="نام نمایشی"
              required
            />
          </div>

          <div className="flex flex-col gap-y-1">
            <label className="text-sm">نام عنصر</label>

            <Input
              type="text"
              name="propertyName"
              defaultValue={property?.propertyName ?? ""}
              placeholder="نام عنصر"
              required
              pattern="[A-Za-z]+"
              title="فقط حروف انگلیسی مجاز است"
            />
          </div>
        </div>

        <div className="flex flex-col gap-y-1">
          <label className="text-sm">توضیحات</label>

          <Textarea
            name="description"
            defaultValue={property?.description ?? ""}
            placeholder="توضیحات..."
            className="resize-none"
          />
        </div>

        <div className="grid grid-cols-2 gap-x-10">
          <div className="flex flex-col gap-y-1">
            <label className="text-sm">نوع عنصر</label>

            <select
              name="type"
              defaultValue={property?.type ?? ""}
              className="h-10 rounded-md border border-slate-300 px-3 py-2 text-sm focus-within:border-primary focus-within:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
              onChange={(e) => {
                setSelectedType(e.target.value as keyof typeof formInputType);
              }}
              required
            >
              <option value="1">عدد</option>
              <option value="3">متن کوتاه</option>
              <option value="4">متن بلند</option>
              <option value="6">زمان</option>
              <option value="5">چک باکس</option>
              <option value="8">رنگ</option>
              <option value="9">ایمیل</option>
              <option value="7">فایل</option>
              <option value="10">پسوورد</option>
              <option value="2">رنج</option>
              <option value="11">نوار کشویی</option>
              <option value="12">ادیتور</option>
              <option value="13">مبلغ</option>
              <option value="14">عکس همراه با پیش نمایش</option>
              <option value="15">ریدیو باتن</option>
            </select>
          </div>

          {selectedType !== "7" && selectedType !== "14" && (
            <div className="flex flex-col gap-y-1">
              <label className="text-sm">مقدار پیش فرض</label>

              <Input
                type={formInputType[selectedType].input}
                name="defaultValue"
                defaultValue={property?.defaultValue ?? ""}
                placeholder="مقدار پیش فرض"
              />
            </div>
          )}

          {(selectedType === "7" || selectedType === "14") && (
            <div className="flex flex-col gap-y-1">
              <label className="text-sm">پسوندهای مجاز</label>

              <Input
                type="text"
                name="allowedFileExtensions"
                defaultValue={property?.allowedFileExtensions ?? ""}
                placeholder="pdf,docx,xlsx"
              />
            </div>
          )}
        </div>

        {(selectedType === "11" || selectedType === "15") && (
          <div className="flex flex-col gap-y-3">
            <label className="text-sm">گزینه ها</label>

            {options.map((option, index) => (
              <div key={index} className="flex items-center gap-2">
                <Input
                  value={option}
                  onChange={(e) => {
                    const cloned = [...options];

                    cloned[index] = e.target.value;

                    setOptions(cloned);
                  }}
                  placeholder={`گزینه ${index + 1}`}
                />

                <Button
                  type="button"
                  variant="destructive"
                  onClick={() => {
                    setOptions((prev) => prev.filter((_, i) => i !== index));
                  }}
                >
                  حذف
                </Button>
              </div>
            ))}

            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setOptions((prev) => [...prev, ""]);
              }}
            >
              افزودن گزینه
            </Button>
          </div>
        )}

        <div className="grid grid-cols-2 gap-x-10">
          <div className="flex flex-col gap-y-1">
            <label className="text-sm">طول کاراکتر (اختیاری)</label>

            <Input type="number" name="length" min={1} placeholder="مثلاً 50" />
          </div>
        </div>

        <div className="mt-5 flex items-center gap-x-2">
          <p className="text-sm text-slate-600">بدون مقدار</p>

          <Switch checked={allowNull} onCheckedChange={setAllowNull} />
        </div>

        <Button type="submit" className="w-full" disabled={isPending}>
          {submitBtnText}
        </Button>
      </form>
    </Modal>
  );
};

export default PropertyModal;